/**
 * NITRIC OXIDE NEW LANDING PAGE - JAVASCRIPT
 * Handles image carousel, pack selection, and add to cart functionality
 */

(function() {
  'use strict';

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    initImageCarousel();
    initPackSelector();
    initQuantitySelector();
    initReviewsScroll();
    initSubscriptionPriceUpdate();
    initAddToCart();
    initBuyNow();
    initMobileEnhancements();
    initStickyButtons();
    initUnitsSold();
    initImageZoom();
    initFbtPopup();
    initOffersCarousel();
    // Swipe runs before the (cosmetic, guarded) label measurement so a throw there can't skip it.
    initOffersSwipe();
    initOffersColonAlign();
  }

  /**
   * OFFERS CAROUSEL
   * Drives the "Available offers" carousel (slides, dots, autoplay). Replaces the
   * snippet's own inline script, which double-initialised each wrapper and caused overlap.
   */
  var OFFERS_AUTOPLAY_DELAY = 8000;

  function initOffersCarousel() {
    document.querySelectorAll('.nitric-offers-carousel-wrapper').forEach(function(wrapper) {
      var track = wrapper.querySelector('.nitric-offers-carousel-track');
      var dotsContainer = wrapper.querySelector('.nitric-offers-carousel-dots');
      if (!track || !dotsContainer) return;

      var slides = track.querySelectorAll('.nitric-offers-carousel-slide');
      var total = slides.length;

      // Fallback in case no slide has `active` (CSS :has() fallback unsupported on older iOS).
      if (total > 0 && !track.querySelector('.nitric-offers-carousel-slide.active')) {
        slides[0].classList.add('active');
      }

      // Single offer (some locales/metafields drop the sale slide): nothing to rotate.
      if (total <= 1) {
        dotsContainer.style.display = 'none';
        return;
      }

      // Start from whichever slide is already marked active, so the two never disagree.
      var current = 0;
      for (var s = 0; s < total; s++) {
        if (slides[s].classList.contains('active')) {
          current = s;
          break;
        }
      }
      slides[current].classList.add('active');

      var interval = null;

      function show(index) {
        slides[current].classList.remove('active');
        current = index;
        slides[current].classList.add('active');
        dotsContainer.querySelectorAll('.nitric-offers-carousel-dot').forEach(function(d, i) {
          d.classList.toggle('active', i === current);
        });
      }

      function next() {
        show((current + 1) % total);
      }

      function startAutoplay() {
        clearInterval(interval);
        interval = setInterval(next, OFFERS_AUTOPLAY_DELAY);
      }

      // Exposed so the swipe handler can advance the carousel without duplicating this.
      wrapper.nitricOffersCarousel = {
        total: total,
        currentIndex: function() { return current; },
        goTo: function(index) {
          if (index === current) return;
          show(index);
          startAutoplay(); // manual navigation restarts the full delay
        }
      };

      dotsContainer.innerHTML = '';
      for (var i = 0; i < total; i++) {
        (function(index) {
          var dot = document.createElement('span');
          dot.className = 'nitric-offers-carousel-dot' + (index === current ? ' active' : '');
          dot.addEventListener('click', function(e) {
            e.preventDefault();
            e.stopPropagation();
            wrapper.nitricOffersCarousel.goTo(index);
          });
          dotsContainer.appendChild(dot);
        })(i);
      }

      startAutoplay();
    });
  }

  /**
   * OFFERS CAROUSEL - MOBILE TOUCH SWIPE
   * A swipe advances to the neighboring dot/slide. Passive listeners, so taps on links still work.
   */
  function initOffersSwipe() {
    var SWIPE_THRESHOLD = 40;
    var wrappers = document.querySelectorAll('.nitric-offers-carousel-wrapper');

    wrappers.forEach(function(wrapper) {
      var startX = 0;
      var startY = 0;
      var tracking = false;

      wrapper.addEventListener('touchstart', function(e) {
        // Ignore multi-touch (pinch/zoom) gestures.
        if (e.touches.length !== 1) {
          tracking = false;
          return;
        }
        tracking = true;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true });

      wrapper.addEventListener('touchend', function(e) {
        if (!tracking) return;
        tracking = false;

        // Mobile only, matching the breakpoint the CSS uses for this carousel.
        if (window.innerWidth > 768) return;

        var deltaX = e.changedTouches[0].clientX - startX;
        var deltaY = e.changedTouches[0].clientY - startY;

        // Too small to be a swipe (a tap on a dot or an offer link).
        if (Math.abs(deltaX) < SWIPE_THRESHOLD) return;
        // Mostly vertical - that's a page scroll, not a slide change.
        if (Math.abs(deltaX) <= Math.abs(deltaY)) return;

        // Resolve target from the DOM, not carousel state - stays safe if that object is missing.
        var dots = wrapper.querySelectorAll('.nitric-offers-carousel-dot');
        var slides = wrapper.querySelectorAll('.nitric-offers-carousel-slide');
        var total = slides.length;
        if (total < 2) return; // nothing to swipe between

        var currentIndex = 0;
        for (var i = 0; i < total; i++) {
          if (slides[i].classList.contains('active')) {
            currentIndex = i;
            break;
          }
        }

        // Swipe left (negative delta) advances. Wraps around, matching autoplay.
        var nextIndex = deltaX < 0 ? currentIndex + 1 : currentIndex - 1;
        if (nextIndex < 0) nextIndex = total - 1;
        if (nextIndex >= total) nextIndex = 0;

        var carousel = wrapper.nitricOffersCarousel;
        if (carousel && typeof carousel.goTo === 'function') {
          // Preferred: moves the slide, syncs dots, restarts autoplay.
          carousel.goTo(nextIndex);
        } else if (dots.length === total) {
          // Equivalent fallback: dots run the same goTo().
          dots[nextIndex].click();
        } else {
          // Last resort: move the class directly if the controller never initialised.
          slides[currentIndex].classList.remove('active');
          slides[nextIndex].classList.add('active');
        }
      }, { passive: true });

      // Resets `tracking` so an interrupted gesture doesn't jump the wrong way later.
      wrapper.addEventListener('touchcancel', function() {
        tracking = false;
      }, { passive: true });
    });
  }

  /**
   * OFFERS COLON ALIGNMENT
   * Wraps each offer label in a fixed-width span so the colons line up into tidy columns.
   */
  function initOffersColonAlign() {
    // Slide 1: bold labels at the start of a paragraph (skip the sale heading).
    var boldLabels = document.querySelectorAll('.nitric-offers-carousel-slide p:not(.nitric-offers-sale-heading) > strong:first-child');
    boldLabels.forEach(function(strong) {
      wrapLabelBeforeColon(strong, 'nitric-offers-align-label');
    });

    // Slide 2: offer items whose leading text node holds the label + colon.
    var items = document.querySelectorAll('.nitric-offers-sale-item');
    items.forEach(function(item) {
      var firstNode = item.firstChild;
      if (!firstNode || firstNode.nodeType !== 3) return; // expect a leading text node
      if (item.querySelector('.nitric-offers-item-label')) return; // already processed
      var text = firstNode.nodeValue;
      var colonIndex = text.indexOf(':');
      if (colonIndex === -1) return;
      var labelText = text.slice(0, colonIndex).replace(/\s+$/, '');
      var rest = text.slice(colonIndex); // starts with ':'
      var span = document.createElement('span');
      span.className = 'nitric-offers-item-label';
      span.textContent = labelText;
      item.insertBefore(document.createTextNode(rest), firstNode);
      item.insertBefore(span, item.firstChild);
      item.removeChild(firstNode);
    });

    // Cosmetic only - wrapped in try/catch so it can never block later init() calls.
    try {
      measureOffersLabelColumn();

      // Re-measure on resize/orientation change since font and viewport affect width.
      var remeasure = debounce(measureOffersLabelColumn, 150);
      window.addEventListener('resize', remeasure);
      window.addEventListener('orientationchange', remeasure);

      // Re-measure once the webfont loads (document.fonts absent on older Safari).
      if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
        document.fonts.ready.then(function() {
          try { measureOffersLabelColumn(); } catch (e) {}
        });
      }
    } catch (e) {
      // Leaves the CSS fallback widths in place; alignment degrades, nothing breaks.
    }
  }

  /**
   * OFFERS LABEL COLUMN MEASUREMENT
   * Measures each label's actual rendered width and sizes the column to the widest one,
   * instead of a hardcoded pixel guess that broke on different fonts/locales/zoom.
   */
  function measureOffersLabelColumn() {
    document.querySelectorAll('.nitric-offers-carousel-wrapper').forEach(function(wrapper) {
      // Desktop keeps the snippet's own fixed column - nothing to do here.
      if (window.innerWidth > 768) return;

      var headLabels = wrapper.querySelectorAll('.nitric-offers-align-label');
      var itemLabels = wrapper.querySelectorAll('.nitric-offers-item-label');
      if (!headLabels.length && !itemLabels.length) return;

      // Collapse the columns first so each label reports its natural text width.
      wrapper.classList.add('nitric-offers-measuring');

      var headMax = widestOf(headLabels);
      var itemMax = widestOf(itemLabels);

      // Removed synchronously so the collapsed state is never painted.
      wrapper.classList.remove('nitric-offers-measuring');

      if (headMax > 0) {
        // Column must fit the label plus the colon glyph and its leading space.
        var colonW = measureColonWidth(wrapper);
        wrapper.style.setProperty('--nitric-offers-label-w', Math.ceil(headMax) + 'px');
        wrapper.style.setProperty('--nitric-offers-col-w', Math.ceil(headMax + colonW) + 'px');
      }

      if (itemMax > 0) {
        wrapper.style.setProperty('--nitric-offers-item-label-w', Math.ceil(itemMax) + 'px');
      }
    });
  }

  function widestOf(els) {
    var max = 0;
    els.forEach(function(el) {
      // Fractional width avoids flooring (which would clip the last glyph).
      var w = el.getBoundingClientRect().width;
      if (w > max) max = w;
    });
    return max;
  }

  /**
   * Width of " :" in the labels' own font, measured by rendering it rather than
   * assumed, so the column stays correct at any font size or family.
   */
  function measureColonWidth(wrapper) {
    var reference = wrapper.querySelector('.nitric-offers-align-label');
    if (!reference) return 8;

    var probe = document.createElement('span');
    probe.textContent = ' :'; // NBSP + colon: the space must not collapse away
    probe.setAttribute('aria-hidden', 'true');
    // Kept in normal flow (off-screen) so it inherits the real font; display:none would not.
    probe.style.cssText = 'position:absolute;visibility:hidden;white-space:pre;pointer-events:none;left:-9999px;top:0;';

    // Appended inside the label's parent so it inherits the same font-weight/size.
    var host = reference.parentNode || wrapper;
    host.appendChild(probe);
    var w = probe.getBoundingClientRect().width;
    host.removeChild(probe);

    return w > 0 ? w : 8;
  }

  function debounce(fn, wait) {
    var t = null;
    return function() {
      clearTimeout(t);
      t = setTimeout(fn, wait);
    };
  }

  function wrapLabelBeforeColon(el, className) {
    if (el.querySelector('.' + className)) return; // already processed
    var text = el.textContent;
    var colonIndex = text.indexOf(':');
    if (colonIndex === -1) return;
    var labelText = text.slice(0, colonIndex).replace(/\s+$/, '');
    var rest = text.slice(colonIndex); // starts with ':'
    var span = document.createElement('span');
    span.className = className;
    span.textContent = labelText;
    el.textContent = '';
    el.appendChild(span);
    el.appendChild(document.createTextNode(rest));
  }

  /**
   * IMAGE CAROUSEL FUNCTIONALITY
   */
  function initImageCarousel() {
    const mainImageEl = document.getElementById('nitricCurrentImage');
    const thumbnails = document.querySelectorAll('.nitric-lp-thumbnail');
    const dots = document.querySelectorAll('.nitric-lp-dot');

    if (!mainImageEl) return;

    var currentImageIndex = 0;
    var isAnimating = false;

    // Preload all images for instant display
    var imageUrls = [];
    dots.forEach(function(dot) {
      var url = dot.getAttribute('data-image');
      if (url) {
        imageUrls.push(url);
        var img = new Image();
        img.src = url;
      }
    });

    // Slide image transition helper
    function changeImage(imageUrl, direction) {
      if (!imageUrl || isAnimating) return;
      if (mainImageEl.src.indexOf(imageUrl.replace(/https?:/, '')) > -1) return;
      isAnimating = true;

      var slideOut = direction === 'left' ? 'nitric-slide-out-left' : 'nitric-slide-out-right';
      var slideIn = direction === 'left' ? 'nitric-slide-in-left' : 'nitric-slide-in-right';

      // Slide out current image
      mainImageEl.classList.add(slideOut);

      setTimeout(function() {
        // Swap the image source
        mainImageEl.src = imageUrl;
        // Sync alt text
        var altDot = document.querySelector('.nitric-lp-dot[data-image="' + imageUrl + '"]');
        if (altDot && altDot.getAttribute('data-alt')) {
          mainImageEl.alt = altDot.getAttribute('data-alt');
        }
        // Position new image off-screen on the incoming side
        mainImageEl.classList.remove(slideOut);
        mainImageEl.classList.add(slideIn);

        // Force reflow then animate in
        void mainImageEl.offsetWidth;
        mainImageEl.classList.remove(slideIn);
        mainImageEl.style.transform = 'translateX(0)';
        mainImageEl.style.opacity = '1';

        setTimeout(function() {
          mainImageEl.style.transform = '';
          mainImageEl.style.opacity = '';
          isAnimating = false;
        }, 350);
      }, 300);
    }

    // Determine direction based on index
    function getDirection(newIndex) {
      return newIndex > currentImageIndex ? 'left' : 'right';
    }

    // Thumbnail click handlers
    thumbnails.forEach(function(thumbnail, index) {
      thumbnail.addEventListener('click', function() {
        const imageUrl = this.getAttribute('data-image');
        if (imageUrl) {
          var dir = getDirection(index);
          changeImage(imageUrl, dir);
          currentImageIndex = index;
          updateActiveStates(index);
        }
      });
    });

    // Dot click handlers
    dots.forEach(function(dot, index) {
      dot.addEventListener('click', function() {
        const imageUrl = this.getAttribute('data-image');
        if (imageUrl) {
          var dir = getDirection(index);
          changeImage(imageUrl, dir);
          currentImageIndex = index;
          updateActiveStates(index);

          // If corresponding thumbnail exists, scroll to it
          if (thumbnails[index]) {
            thumbnails[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
          }
        }
      });
    });

    // Update active states helper
    function updateActiveStates(activeIndex) {
      // Update thumbnails
      thumbnails.forEach(function(thumb, i) {
        if (i === activeIndex) {
          thumb.classList.add('active');
        } else {
          thumb.classList.remove('active');
        }
      });

      // Update dots
      dots.forEach(function(dot, i) {
        if (i === activeIndex) {
          dot.classList.add('active');
        } else {
          dot.classList.remove('active');
        }
      });
    }
  }

  /**
   * PACK SELECTOR FUNCTIONALITY
   */
  function initPackSelector() {
    const packCards = document.querySelectorAll('.nitric-lp-pack-card');
    const variantIdInput = document.getElementById('nitricVariantId');
    const priceEl = document.getElementById('nitricCurrentPrice');
    const compareEl = document.getElementById('nitricComparePrice');
    const saveBadgeEl = document.getElementById('nitricSaveBadge');
    const wrapper = document.querySelector('[data-compare-raw]');
    const mainImageEl = document.getElementById('nitricCurrentImage');

    packCards.forEach(function(card) {
      card.addEventListener('click', function() {
        const variantId = this.getAttribute('data-variant-id');
        const onetimePrice = parseInt(this.getAttribute('data-price'), 10);
        const comparePrice = parseInt(this.getAttribute('data-compare-price'), 10);
        const savings = parseInt(this.getAttribute('data-savings'), 10);

        // Show only this pack's bottle image (plus all shared images) in the gallery,
        // then make this pack's bottle image the active/main image.
        filterGalleryByPack(variantId);

        // Update active state
        packCards.forEach(function(c) {
          c.classList.remove('active');
        });
        this.classList.add('active');

        // Update hidden variant ID input
        if (variantIdInput) {
          variantIdInput.value = variantId;
        }

        // Update the wrapper's data-compare-raw so subscription logic uses correct compare
        if (wrapper) {
          wrapper.setAttribute('data-compare-raw', comparePrice / 100);
          wrapper.setAttribute('data-variant-price-cents', onetimePrice);
        }

        // Check if subscription is currently selected
        var isSub = isSubscriptionCurrentlySelected();
        var displayPrice = onetimePrice;

        if (isSub) {
          var subPriceText = this.getAttribute('data-sub-price');
          if (subPriceText) {
            var subPriceCents = parsePriceToCents(subPriceText);
            if (subPriceCents > 0) {
              displayPrice = subPriceCents;
            }
          }
        }

        // Update displayed price
        if (priceEl) {
          priceEl.textContent = formatMoney(displayPrice);
        }

        // Update compare price
        if (compareEl) {
          if (comparePrice > displayPrice) {
            compareEl.textContent = formatMoney(comparePrice);
            compareEl.style.display = 'inline';
          } else {
            compareEl.style.display = 'none';
          }
        }

        // Update savings badge
        if (saveBadgeEl) {
          var effectiveSavings = savings;
          if (comparePrice > 0 && displayPrice > 0) {
            effectiveSavings = Math.round(((comparePrice - displayPrice) / comparePrice) * 100);
          }
          if (effectiveSavings > 0) {
            saveBadgeEl.textContent = '(' + effectiveSavings + ' %OFF)';
            saveBadgeEl.style.display = 'inline-block';
          } else {
            saveBadgeEl.style.display = 'none';
          }
        }

        // Update URL for cart purposes (do NOT dispatch variant:change to avoid Loop reset)
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url.toString());

        // Update all pack card prices and savings to reflect current purchase option
        updateAllPackCardPrices(isSub);

        // Switch the Loop widget over to this variant in place (no page reload) and
        // re-apply whichever purchase option was selected before the switch.
        syncLoopWidgetToVariant(variantId, isSub ? 'subscription' : 'onetime');
      });
    });

    // Apply the initial gallery filter for the pack active on page load
    var initialActiveCard = document.querySelector('.nitric-lp-pack-card.active') || packCards[0];
    if (initialActiveCard) {
      filterGalleryByPack(initialActiveCard.getAttribute('data-variant-id'));
    }
  }

  /**
   * GALLERY PACK FILTER + ORDERING
   * On pack select, moves that pack's bottle dot to the front and hides other packs'
   * bottle dots; shared images stay visible in their authored order.
   */
  var nitricOriginalDotOrder = null;

  // Purchase option ('subscription'|'onetime') syncLoopWidgetToVariant's retry loop is
  // currently enforcing. Shared/mutable so a real shopper click updates it immediately.
  var nitricLoopDesiredOption = null;

  // Shopper's chosen delivery-plan id, since Loop resets the frequency <select> to its
  // first option on every purchase-option click - this lets the retry loop restore it.
  var nitricLoopDesiredSellingPlanId = null;

  function filterGalleryByPack(variantId) {
    var mainImageEl = document.getElementById('nitricCurrentImage');
    var dotsContainer = document.getElementById('nitricCarouselDots');
    if (!dotsContainer) return;

    // Snapshot the authored dot order once; every reorder rebuilds from this, not the DOM.
    if (!nitricOriginalDotOrder) {
      nitricOriginalDotOrder = Array.prototype.slice.call(
        dotsContainer.querySelectorAll('.nitric-lp-dot')
      );
    }

    var dots = nitricOriginalDotOrder;
    if (!dots.length) return;

    variantId = variantId ? String(variantId) : '';
    var packBottleDot = null;

    dots.forEach(function(dot) {
      if (dot.getAttribute('data-bottle-variant') === variantId) {
        packBottleDot = dot;
      }
    });

    if (!packBottleDot) return;

    // Hide other packs' bottle dots; keep this pack's bottle dot and shared dots visible.
    dots.forEach(function(dot) {
      var bottleVariant = dot.getAttribute('data-bottle-variant');
      var hide = bottleVariant && bottleVariant !== variantId;
      dot.classList.toggle('nitric-lp-dot--hidden', !!hide);
    });

    // Rebuild the strip as [selected bottle dot, ...every other dot in authored order].
    var fragment = document.createDocumentFragment();
    fragment.appendChild(packBottleDot);
    dots.forEach(function(dot) {
      if (dot !== packBottleDot) fragment.appendChild(dot);
    });
    dotsContainer.appendChild(fragment);

    // The promoted bottle image becomes the active/main image.
    dots.forEach(function(dot) {
      dot.classList.toggle('active', dot === packBottleDot);
    });

    var newImg = packBottleDot.getAttribute('data-image');
    if (mainImageEl && newImg) {
      mainImageEl.src = newImg;
      // Sync alt text
      if (packBottleDot.getAttribute('data-alt')) {
        mainImageEl.alt = packBottleDot.getAttribute('data-alt');
      }
    }
  }

  function isSubscriptionCurrentlySelected() {
    var fieldset = document.getElementById('loop-selling-plan-fieldset');
    if (!fieldset) return false;
    // Check all subscription radios - the checked one may not be the first in the fieldset.
    return isSubscriptionCheckedAnywhere();
  }

  /**
   * LOOP WIDGET <-> PACK SYNC (NO PAGE RELOAD)
   * Drives Loop's variant switch explicitly (its own MutationObserver doesn't
   * reliably fire), then re-applies the shopper's purchase option since Loop resets
   * to its own default on every re-init.
   */
  function getLoopProductId() {
    if (window.nitricLpProductData && window.nitricLpProductData.productId) {
      return window.nitricLpProductData.productId;
    }
    return null;
  }

  // Scan all subscription radios (shared name="loop_purchase_option") - the checked one may belong to another variant.
  function isSubscriptionCheckedAnywhere() {
    var subRadios = document.querySelectorAll('.loop-subscription-group-radio');
    for (var i = 0; i < subRadios.length; i++) {
      if (subRadios[i].checked) return true;
    }
    return false;
  }

  function findPurchaseOptionRadio(option, variantId) {
    var fieldset = document.getElementById('loop-selling-plan-fieldset');
    if (!fieldset) return null;

    if (option === 'onetime') {
      return fieldset.querySelector('.loop-one-time-purchase-option-radio');
    }

    // Prefer the subscription group belonging to the variant now selected.
    var container = variantId ? document.getElementById('loop-product-variant-' + variantId) : null;
    var radio = container ? container.querySelector('.loop-subscription-group-radio') : null;
    return radio || fieldset.querySelector('.loop-subscription-group-radio');
  }

  function applyPurchaseOption(option, variantId) {
    var radio = findPurchaseOptionRadio(option, variantId);
    if (!radio) return false;
    if (!radio.checked) {
      // Real click so Loop's own handlers run, not just flipping .checked.
      radio.click();
    }
    return true;
  }

  // Re-asserts the shopper's delivery frequency, undoing Loop's reset-to-first-option.
  // Only applies if the new pack actually offers that same plan; otherwise leaves Loop's default.
  function applyDesiredFrequency(variantId) {
    if (!nitricLoopDesiredSellingPlanId) return;

    var container = document.getElementById('loop-product-variant-' + variantId);
    if (!container) return;

    var select = container.querySelector('.loop-selling-plan-selector');
    if (!select) return;

    var hasOption = false;
    for (var i = 0; i < select.options.length; i++) {
      if (select.options[i].value === nitricLoopDesiredSellingPlanId) {
        hasOption = true;
        break;
      }
    }
    if (!hasOption) return;

    if (select.value !== nitricLoopDesiredSellingPlanId) {
      select.value = nitricLoopDesiredSellingPlanId;
      // Real change event so Loop's own handler (price/badge refresh) runs.
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function syncLoopWidgetToVariant(variantId, desiredOption) {
    var fieldset = document.getElementById('loop-selling-plan-fieldset');

    // No selling plans / Loop not present - nothing to sync.
    if (!fieldset) return;

    // Keep Loop's hidden variant input in step with the pack we just selected.
    var loopVariantInput = document.querySelector('[name="loop_variant_id"]');
    if (loopVariantInput) loopVariantInput.value = variantId;

    var productId = getLoopProductId();
    var loopProduct = productId && window.loopProps && window.loopProps[productId]
      ? window.loopProps[productId].product
      : null;

    // Shared so any still-running retry loop reads this fresh, not a stale snapshot.
    nitricLoopDesiredOption = desiredOption;

    // Ask Loop to re-init for this variant: toggles the visible block, refreshes prices.
    if (loopProduct && typeof window.loopVariantChanged === 'function') {
      try {
        window.loopVariantChanged({ loopProduct: loopProduct, variantId: variantId });
      } catch (e) {
        // If Loop's internals change, fall back to re-applying the option below.
      }
    }

    // Retried briefly since Loop's re-init is partly async and re-applies its own
    // default; reads nitricLoopDesiredOption fresh each tick so a real shopper click
    // mid-retry takes priority over the option desired when this loop started.
    var attempts = 0;
    var maxAttempts = 12; // ~1.8s at 150ms

    (function reapply() {
      attempts++;
      var currentDesired = nitricLoopDesiredOption;
      var isSubNow = isSubscriptionCheckedAnywhere();
      var matches = currentDesired === 'subscription' ? isSubNow : !isSubNow;

      if (!matches) applyPurchaseOption(currentDesired, variantId);

      // Re-assert frequency only once subscription is confirmed, after the radio click above.
      if (currentDesired === 'subscription') {
        applyDesiredFrequency(variantId);
      }

      if (attempts < maxAttempts) {
        setTimeout(reapply, 150);
      } else {
        // Final sync of price display / pack cards to the settled state.
        refreshPriceForCurrentSelection();
      }
    })();
  }

  // Re-runs the price/pack-card sync for programmatic selection changes.
  function refreshPriceForCurrentSelection() {
    var isSub = isSubscriptionCheckedAnywhere();
    updateAllPackCardPrices(isSub);

    var activeCard = document.querySelector('.nitric-lp-pack-card.active');
    var priceEl = document.getElementById('nitricCurrentPrice');
    if (!activeCard || !priceEl) return;

    var displayCents = parseInt(activeCard.getAttribute('data-price'), 10);
    if (isSub) {
      var subCents = parsePriceToCents(activeCard.getAttribute('data-sub-price') || '');
      if (subCents > 0) displayCents = subCents;
    }
    if (!(displayCents > 0)) return;

    priceEl.textContent = formatMoney(displayCents);

    var compareCents = parseInt(activeCard.getAttribute('data-compare-price'), 10);
    var compareEl = document.getElementById('nitricComparePrice');
    if (compareEl) {
      if (compareCents > displayCents) {
        compareEl.textContent = formatMoney(compareCents);
        compareEl.style.display = 'inline';
      } else {
        compareEl.style.display = 'none';
      }
    }

    var saveBadgeEl = document.getElementById('nitricSaveBadge');
    if (saveBadgeEl) {
      var percent = compareCents > 0
        ? Math.round(((compareCents - displayCents) / compareCents) * 100)
        : 0;
      if (percent > 0) {
        saveBadgeEl.textContent = '(' + percent + ' %OFF)';
        saveBadgeEl.style.display = 'inline-block';
      } else {
        saveBadgeEl.style.display = 'none';
      }
    }
  }

  function parsePriceToCents(priceText) {
    var cleaned = priceText.replace(/[^0-9.,]/g, '');
    if (cleaned.indexOf(',') > -1 && cleaned.indexOf('.') > -1) {
      if (cleaned.lastIndexOf(',') > cleaned.lastIndexOf('.')) {
        cleaned = cleaned.replace(/\./g, '').replace(',', '.');
      } else {
        cleaned = cleaned.replace(/,/g, '');
      }
    } else if (cleaned.indexOf(',') > -1) {
      var parts = cleaned.split(',');
      if (parts[parts.length - 1].length <= 2) {
        cleaned = cleaned.replace(/,/g, '.');
      } else {
        cleaned = cleaned.replace(/,/g, '');
      }
    }
    return Math.round(parseFloat(cleaned) * 100) || 0;
  }

  function updateAllPackCardPrices(isSub) {
    var packCards = document.querySelectorAll('.nitric-lp-pack-card[data-onetime-price]');
    packCards.forEach(function(card) {
      var priceDiv = card.querySelector('.nitric-lp-pack-price');
      if (priceDiv) {
        priceDiv.textContent = isSub ? card.getAttribute('data-sub-price') : card.getAttribute('data-onetime-price');
      }
      // Update savings badge inside each pack card
      var savingsDiv = card.querySelector('.nitric-lp-pack-savings');
      if (savingsDiv) {
        var percent = isSub ? card.getAttribute('data-sub-savings') : card.getAttribute('data-savings');
        if (percent && parseInt(percent, 10) > 0) {
          savingsDiv.textContent = 'Save ' + percent + '%';
          savingsDiv.style.display = '';
        } else {
          savingsDiv.style.display = 'none';
        }
      }
    });
  }

  /**
   * QUANTITY SELECTOR FUNCTIONALITY
   */
  function initQuantitySelector() {
    const qtyInput = document.getElementById('nitricQuantity');
    const minusBtn = document.querySelector('.nitric-lp-qty-minus');
    const plusBtn = document.querySelector('.nitric-lp-qty-plus');

    if (!qtyInput) return;

    // Plus button
    if (plusBtn) {
      plusBtn.addEventListener('click', function() {
        const currentValue = parseInt(qtyInput.value, 10) || 1;
        qtyInput.value = currentValue + 1;
      });
    }

    // Minus button
    if (minusBtn) {
      minusBtn.addEventListener('click', function() {
        const currentValue = parseInt(qtyInput.value, 10) || 1;
        if (currentValue > 1) {
          qtyInput.value = currentValue - 1;
        }
      });
    }

    // Block non-numeric key entry (letters, symbols, e/+/-/. etc.)
    qtyInput.addEventListener('keydown', function(e) {
      const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
      if (allowedKeys.indexOf(e.key) !== -1 || e.ctrlKey || e.metaKey) {
        return;
      }
      if (!/^[0-9]$/.test(e.key)) {
        e.preventDefault();
      }
    });

    // Strip any non-numeric characters that slip through (e.g. paste)
    qtyInput.addEventListener('input', function() {
      const sanitized = this.value.replace(/[^0-9]/g, '');
      if (sanitized !== this.value) {
        this.value = sanitized;
      }
    });

    // Validate input
    qtyInput.addEventListener('change', function() {
      const value = parseInt(this.value, 10);
      if (isNaN(value) || value < 1) {
        this.value = 1;
      }
    });
  }

  /**
   * REVIEWS SCROLL FUNCTIONALITY
   */
  function initReviewsScroll() {
    const reviewsBadge = document.getElementById('nitricLpReviewsBadge');
    const reviewsSection = document.getElementById('nitricLpReviewsSection');

    if (reviewsBadge && reviewsSection) {
      reviewsBadge.addEventListener('click', function() {
        reviewsSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      });

      // Make it look clickable
      reviewsBadge.style.cursor = 'pointer';
    }
  }

  /**
   * SUBSCRIPTION PRICE UPDATE
   * Integrates with Loop Subscriptions widget
   */
  function initSubscriptionPriceUpdate() {
    const priceEl = document.querySelector('[data-product-price]');
    const discountEl = document.querySelector('[data-discount-percent]');
    const wrapper = document.querySelector('[data-compare-raw]');

    if (!priceEl) return;

    function getComparePriceDollars() {
      return wrapper ? parseFloat(wrapper.getAttribute('data-compare-raw')) : 0;
    }

    function getActivePackCard() {
      return document.querySelector('.nitric-lp-pack-card.active');
    }

    function getCurrentVariantId() {
      var input = document.getElementById('nitricVariantId');
      if (input) return input.value;
      if (window.nitricLpProductData) return window.nitricLpProductData.variantId;
      return null;
    }

    function parsePrice(text) {
      var cleaned = text.replace(/[^0-9.,]/g, '');
      if (cleaned.indexOf(',') > -1 && cleaned.indexOf('.') > -1) {
        if (cleaned.lastIndexOf(',') > cleaned.lastIndexOf('.')) {
          cleaned = cleaned.replace(/\./g, '').replace(',', '.');
        } else {
          cleaned = cleaned.replace(/,/g, '');
        }
      } else if (cleaned.indexOf(',') > -1) {
        var parts = cleaned.split(',');
        if (parts[parts.length - 1].length <= 2) {
          cleaned = cleaned.replace(/,/g, '.');
        } else {
          cleaned = cleaned.replace(/,/g, '');
        }
      }
      return parseFloat(cleaned) || 0;
    }

    function getActiveVariantContainer() {
      var variantId = getCurrentVariantId();
      var container = document.getElementById('loop-product-variant-' + variantId);
      if (container) return container;
      var all = document.querySelectorAll('[id^="loop-product-variant-"]');
      for (var i = 0; i < all.length; i++) {
        if (all[i].style.display !== 'none') return all[i];
      }
      return null;
    }

    function isSubscriptionSelected() {
      var variantContainer = getActiveVariantContainer();
      var fieldset = document.getElementById('loop-selling-plan-fieldset');
      var subRadio = variantContainer ? variantContainer.querySelector('.loop-subscription-group-radio') : (fieldset ? fieldset.querySelector('.loop-subscription-group-radio') : null);
      return subRadio && subRadio.checked;
    }

    function updatePdpPrice() {
      var fieldset = document.getElementById('loop-selling-plan-fieldset');
      if (!fieldset) return;

      var variantContainer = getActiveVariantContainer();
      var oneTimeRadio = fieldset.querySelector('.loop-one-time-purchase-option-radio');
      var subRadio = variantContainer ? variantContainer.querySelector('.loop-subscription-group-radio') : fieldset.querySelector('.loop-subscription-group-radio');
      var selectedPriceDollars = 0;
      var isSub = subRadio && subRadio.checked;

      // Get the active pack's compare price (dynamic, not fixed)
      var comparePriceDollars = getComparePriceDollars();
      var activeCard = getActivePackCard();
      if (activeCard) {
        var cardCompare = parseInt(activeCard.getAttribute('data-compare-price'), 10);
        if (cardCompare > 0) {
          comparePriceDollars = cardCompare / 100;
        }
      }

      if (isSub) {
        // For subscription, use the active pack's sub price
        if (activeCard) {
          var subPriceText = activeCard.getAttribute('data-sub-price');
          if (subPriceText) {
            selectedPriceDollars = parsePrice(subPriceText);
            priceEl.innerHTML = subPriceText;
          }
        }
        // Fallback to Loop widget price if no pack card data
        if (!selectedPriceDollars) {
          var subPriceEl = variantContainer ? variantContainer.querySelector('.loop-subscription-group-price-amount') : fieldset.querySelector('.loop-subscription-group-price-amount');
          if (subPriceEl) {
            priceEl.innerHTML = subPriceEl.textContent.trim();
            selectedPriceDollars = parsePrice(subPriceEl.textContent);
          }
        }
      } else if (oneTimeRadio && oneTimeRadio.checked) {
        // For one-time, use the active pack's one-time price
        if (activeCard) {
          var onetimePrice = parseInt(activeCard.getAttribute('data-price'), 10);
          if (onetimePrice > 0) {
            selectedPriceDollars = onetimePrice / 100;
            priceEl.innerHTML = formatMoney(onetimePrice);
          }
        }
        // Fallback to Loop widget price
        if (!selectedPriceDollars) {
          var otPriceEl = document.getElementById('loop-price-one-time');
          if (otPriceEl) {
            priceEl.innerHTML = otPriceEl.textContent.trim();
            selectedPriceDollars = parsePrice(otPriceEl.textContent);
          }
        }
      }

      if (discountEl && comparePriceDollars > 0 && selectedPriceDollars > 0) {
        var percent = Math.round(((comparePriceDollars - selectedPriceDollars) / comparePriceDollars) * 100);
        if (percent > 0) {
          discountEl.textContent = '(' + percent + ' %OFF)';
          discountEl.style.display = 'inline';
        } else {
          discountEl.style.display = 'none';
        }
      }

      updatePackPrices(isSub);
    }

    function updatePackPrices(isSub) {
      updateAllPackCardPrices(isSub);
    }

    // Listen for Loop widget changes
    document.addEventListener('click', function(e) {
      var radio = e.target.closest('[name="loop_purchase_option"]') || e.target.closest('.loop-one-time-purchase-option') || e.target.closest('.loop-subscription-group');
      if (radio) {
        // Loop's own click handler already reset the frequency <select> by this point
        // (bubble phase runs after target phase), so re-assert it immediately here.
        if (isSubscriptionCheckedAnywhere()) {
          applyDesiredFrequency(getCurrentVariantId());
        }

        setTimeout(updatePdpPrice, 100);

        // isTrusted excludes this file's/loop-widget.js's own programmatic radio.click()
        // calls, so only a real shopper choice becomes what the retry loop enforces.
        if (e.isTrusted) {
          nitricLoopDesiredOption = isSubscriptionCheckedAnywhere() ? 'subscription' : 'onetime';
        }
      }
    });

    // Records the shopper's frequency choice so applyDesiredFrequency() can restore it later.
    document.addEventListener('change', function(e) {
      if (e.isTrusted && e.target.classList && e.target.classList.contains('loop-selling-plan-selector')) {
        nitricLoopDesiredSellingPlanId = e.target.value;
      }
    });
  }

  /**
   * ADD TO CART FUNCTIONALITY
   */
  function initAddToCart() {
    const form = document.getElementById('nitric-lp-add-to-cart-form');
    const addToCartBtn = document.getElementById('nitricAddToCartBtn');

    if (!form || !addToCartBtn) return;

    form.addEventListener('submit', function(e) {
      e.preventDefault();

      const formData = new FormData(form);
      const variantId = formData.get('id');
      const quantity = formData.get('quantity') || 1;

      // Get selected selling plan from Loop widget
      const sellingPlanInput = document.querySelector('[name="selling_plan"]');
      const sellingPlan = sellingPlanInput ? sellingPlanInput.value : null;

      // Show loading state
      addToCartBtn.classList.add('loading');
      addToCartBtn.disabled = true;

      // Prepare cart data
      const cartData = {
        items: [{
          id: variantId,
          quantity: parseInt(quantity, 10)
        }]
      };

      // Add selling plan if selected
      if (sellingPlan) {
        cartData.items[0].selling_plan = sellingPlan;
      }

      // Add to cart via AJAX
      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(cartData)
      })
      .then(function(response) {
        return response.json();
      })
      .then(function(data) {
        // Success - redirect to cart or show success message
        // Option 1: Redirect to cart
        window.location.href = window.Shopify.routes.root + 'cart';

        // Option 2: Show success message and refresh cart drawer
        // showSuccessMessage();
        // refreshCartDrawer();
      })
      .catch(function(error) {
        console.error('Error adding to cart:', error);
        alert('Error adding to cart. Please try again.');
      })
      .finally(function() {
        // Remove loading state
        addToCartBtn.classList.remove('loading');
        addToCartBtn.disabled = false;
      });
    });
  }

  /**
   * HELPER FUNCTIONS
   */

  // Format money based on Shopify's money format
  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, Shopify.money_format || '${{amount}}');
    }

    // Fallback using shop money format from Liquid
    var format = (window.nitricLpProductData && window.nitricLpProductData.moneyFormat) ? window.nitricLpProductData.moneyFormat : '${{amount}}';
    var amount = (cents / 100).toFixed(2);
    var amountNoDecimals = Math.round(cents / 100).toString();
    var amountWithComma = amount.replace('.', ',');

    return format
      .replace('{{amount_with_comma_separator}}', amountWithComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals)
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount}}', amount);
  }

  // Show success message (optional implementation)
  function showSuccessMessage() {
    // Create and show a toast notification
    const toast = document.createElement('div');
    toast.className = 'nitric-lp-toast';
    toast.textContent = '✓ Added to cart!';
    toast.style.cssText = 'position:fixed;top:20px;right:20px;background:#28a745;color:#fff;padding:16px 24px;border-radius:8px;font-weight:600;z-index:9999;animation:slideInRight 0.3s ease;';
    document.body.appendChild(toast);

    setTimeout(function() {
      toast.style.animation = 'slideOutRight 0.3s ease';
      setTimeout(function() {
        document.body.removeChild(toast);
      }, 300);
    }, 3000);
  }

  // Refresh cart drawer (if theme has one)
  function refreshCartDrawer() {
    if (typeof theme !== 'undefined' && theme.cart && theme.cart.refresh) {
      theme.cart.refresh();
    }

    // Trigger cart update event
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  /**
   * BUY NOW FUNCTIONALITY
   */
  function initBuyNow() {
    const buyNowBtn = document.getElementById('nitricBuyNowBtn');
    if (!buyNowBtn) return;

    // On BFCache restore (Back/Forward), the DOM is frozen mid-"loading" since no
    // script re-runs - e.persisted flags that restore, so reset the button here.
    window.addEventListener('pageshow', function(e) {
      if (e.persisted) {
        buyNowBtn.classList.remove('loading');
        buyNowBtn.disabled = false;
      }

      // Checkout may have added items (e.g. package protection) to the live cart.
      // e.persisted isn't reliably true on every Back, so refresh on every pageshow
      // instead - same cart:refresh event product-nitric-hero.liquid already uses to
      // sync the Hulk Apps slide-cart widget. Harmless no-op if nothing changed.
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    });

    buyNowBtn.addEventListener('click', function() {
      const variantIdInput = document.getElementById('nitricVariantId');
      const qtyInput = document.getElementById('nitricQuantity');
      const sellingPlanInput = document.querySelector('[name="selling_plan"]');

      if (!variantIdInput) return;

      const variantId = variantIdInput.value;
      const quantity = qtyInput ? parseInt(qtyInput.value, 10) : 1;
      const sellingPlan = sellingPlanInput ? sellingPlanInput.value : null;

      // Show loading state
      buyNowBtn.classList.add('loading');
      buyNowBtn.disabled = true;

      const rootUrl = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';

      // Same cart/add.js call initAddToCart uses, so the item lands in the shopper's
      // normal session cart and is still there in /cart if they back out of checkout.
      const cartData = {
        items: [{
          id: variantId,
          quantity: quantity
        }]
      };
      if (sellingPlan) {
        cartData.items[0].selling_plan = sellingPlan;
      }

      fetch(rootUrl + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(cartData)
      })
      .then(function(response) {
        return response.json();
      })
      .then(function(data) {
        if (data.status) {
          // cart/add.js returns errors as a JSON body, not a rejected fetch.
          throw new Error(data.description || 'Failed to add to cart');
        }
        window.location.href = rootUrl + 'checkout';
      })
      .catch(function(error) {
        console.error('Error:', error);
        alert('Error processing checkout. Please try again.');
        buyNowBtn.classList.remove('loading');
        buyNowBtn.disabled = false;
      });
    });
  }

  /**
   * MOBILE ENHANCEMENTS
   */
  function initMobileEnhancements() {
    // Thumbnail horizontal scroll on mobile
    const thumbnailsContainer = document.querySelector('.nitric-lp-thumbnails');
    if (thumbnailsContainer && window.innerWidth <= 768) {
      // Enable smooth scrolling
      thumbnailsContainer.style.scrollBehavior = 'smooth';

      // Center active thumbnail on scroll
      const activeThumbnail = thumbnailsContainer.querySelector('.nitric-lp-thumbnail.active');
      if (activeThumbnail) {
        activeThumbnail.scrollIntoView({ inline: 'center', block: 'nearest' });
      }
    }

    // Touch swipe for image carousel on mobile
    if (window.innerWidth <= 768) {
      const mainImage = document.getElementById('nitricMainImage');
      const dots = document.querySelectorAll('.nitric-lp-dot');

      if (mainImage && dots.length > 0) {
        let touchStartX = 0;
        let touchEndX = 0;

        mainImage.addEventListener('touchstart', function(e) {
          touchStartX = e.changedTouches[0].screenX;
        }, { passive: true });

        mainImage.addEventListener('touchend', function(e) {
          touchEndX = e.changedTouches[0].screenX;
          handleSwipe();
        }, { passive: true });

        function handleSwipe() {
          const swipeThreshold = 50;
          const diff = touchStartX - touchEndX;

          if (Math.abs(diff) > swipeThreshold) {
            // Re-query current DOM order (pack selection moves the bottle dot); skip hidden dots.
            const orderedDots = Array.prototype.filter.call(
              document.querySelectorAll('.nitric-lp-dot'),
              function(dot) { return !dot.classList.contains('nitric-lp-dot--hidden'); }
            );
            if (orderedDots.length < 2) return;

            const activeDot = document.querySelector('.nitric-lp-dot.active');
            let currentIndex = Array.prototype.indexOf.call(orderedDots, activeDot);
            if (currentIndex === -1) currentIndex = 0;

            if (diff > 0 && currentIndex < orderedDots.length - 1) {
              // Swipe left - next image
              orderedDots[currentIndex + 1].click();
            } else if (diff < 0 && currentIndex > 0) {
              // Swipe right - previous image
              orderedDots[currentIndex - 1].click();
            }
          }
        }
      }
    }

    // Prevent zoom on double tap for buttons
    const buttons = document.querySelectorAll('.nitric-lp-add-to-cart-btn, .nitric-lp-buy-now-btn');
    buttons.forEach(function(button) {
      let lastTouchEnd = 0;
      button.addEventListener('touchend', function(e) {
        const now = Date.now();
        if (now - lastTouchEnd <= 300) {
          e.preventDefault();
        }
        lastTouchEnd = now;
      }, { passive: false });
    });

    // Improve scrolling performance on mobile
    if ('scrollBehavior' in document.documentElement.style) {
      document.documentElement.style.scrollBehavior = 'smooth';
    }

    // Pack selector - center selected on mobile
    const packCards = document.querySelectorAll('.nitric-lp-pack-card');
    packCards.forEach(function(card) {
      card.addEventListener('click', function() {
        if (window.innerWidth <= 768) {
          // Small delay to allow active class to be added
          setTimeout(function() {
            const activeCard = document.querySelector('.nitric-lp-pack-card.active');
            if (activeCard) {
              activeCard.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            }
          }, 100);
        }
      });
    });

    // Adjust viewport on orientation change
    window.addEventListener('orientationchange', function() {
      setTimeout(function() {
        window.scrollTo(0, window.scrollY + 1);
        window.scrollTo(0, window.scrollY - 1);
      }, 100);
    });
  }

  /**
   * STICKY BOTTOM ACTION BAR (MOBILE ONLY)
   * Shows sticky Add to Cart / Buy Now buttons when main buttons scroll out of view
   */
  function initStickyButtons() {
    // Only initialize on mobile devices
    if (window.innerWidth > 768) return;

    const stickyBar = document.getElementById('nitricMobileStickyBar');
    const mainButtonGroup = document.querySelector('.nitric-lp-button-group');
    const stickyAddToCart = document.getElementById('nitricStickyAddToCart');
    const stickyBuyNow = document.getElementById('nitricStickyBuyNow');
    const mainAddToCart = document.getElementById('nitricAddToCartBtn');
    const mainBuyNow = document.getElementById('nitricBuyNowBtn');

    if (!stickyBar || !mainButtonGroup || !stickyAddToCart || !stickyBuyNow) return;

    // Sticky bar stays permanently visible on mobile (CSS position:fixed).
    // Proxy clicks delegate to the real Add to Cart / Buy Now buttons so all
    // existing variant/pack, loading, disabled, and tracking logic runs there.
    stickyAddToCart.addEventListener('click', function() {
      if (mainAddToCart) {
        mainAddToCart.click();
      }
    });

    stickyBuyNow.addEventListener('click', function() {
      if (mainBuyNow) {
        mainBuyNow.click();
      }
    });
  }

  function initUnitsSold() {
    var el = document.getElementById('nitricUnitsSold');
    if (!el) return;
    var units = parseInt(el.getAttribute('data-units'), 10);
    if (!isNaN(units)) {
      el.textContent = units.toLocaleString('en-US') + ' units sold';
    }
  }

  /**
   * FBT PRODUCT POPUP
   * Click on Frequently Bought Together card to show product details popup
   */
  function initFbtPopup() {
    var overlay = document.getElementById('nitricFbtPopupOverlay');
    var closeBtn = document.getElementById('nitricFbtPopupClose');
    var loadingEl = document.getElementById('nitricFbtPopupLoading');
    var contentEl = document.getElementById('nitricFbtPopupContent');
    var imgEl = document.getElementById('nitricFbtPopupImg');
    var titleEl = document.getElementById('nitricFbtPopupTitle');
    var priceEl = document.getElementById('nitricFbtPopupPrice');
    var compareEl = document.getElementById('nitricFbtPopupCompare');
    var descEl = document.getElementById('nitricFbtPopupDesc');
    var variantsEl = document.getElementById('nitricFbtPopupVariants');
    var atcBtn = document.getElementById('nitricFbtPopupAtc');
    var thumbsEl = document.getElementById('nitricFbtPopupThumbs');
    var fbtCards = document.querySelectorAll('.nitric-lp-fbt-card[data-product-handle]');

    if (!overlay || !fbtCards.length) return;

    var selectedVariantId = null;

    fbtCards.forEach(function(card) {
      card.addEventListener('click', function(e) {
        e.preventDefault();
        var handle = this.getAttribute('data-product-handle');
        var productUrl = this.getAttribute('data-product-url');
        if (handle) openPopup(handle, productUrl);
      });
    });

    function openPopup(handle, productUrl) {
      overlay.classList.add('active');
      document.body.style.overflow = 'hidden';
      loadingEl.classList.add('active');
      contentEl.classList.remove('active');

      var rootUrl = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
      fetch(rootUrl + 'products/' + handle + '.js')
        .then(function(res) { return res.json(); })
        .then(function(product) {
          renderPopup(product, productUrl);
        })
        .catch(function() {
          closePopup();
          if (productUrl) window.location.href = productUrl;
        });
    }

    function renderPopup(product, productUrl) {
      // Image
      if (product.featured_image) {
        imgEl.src = product.featured_image;
        imgEl.alt = product.title;
      }

      // Thumbnails - horizontal scroll of all product images
      thumbsEl.innerHTML = '';
      var dotsEl = document.getElementById('nitricFbtPopupDots');
      if (dotsEl) dotsEl.innerHTML = '';
      if (product.images && product.images.length > 1) {
        product.images.forEach(function(imgUrl, i) {
          var thumb = document.createElement('div');
          thumb.className = 'nitric-lp-fbt-popup-thumb' + (i === 0 ? ' active' : '');
          var thumbImg = document.createElement('img');
          thumbImg.src = imgUrl;
          thumbImg.alt = product.title + ' image ' + (i + 1);
          thumb.appendChild(thumbImg);
          thumb.addEventListener('click', function() {
            imgEl.src = imgUrl;
            thumbsEl.querySelectorAll('.nitric-lp-fbt-popup-thumb').forEach(function(t) {
              t.classList.remove('active');
            });
            thumb.classList.add('active');
            if (dotsEl) {
              dotsEl.querySelectorAll('.nitric-lp-fbt-popup-dot').forEach(function(d) { d.classList.remove('active'); });
              dotsEl.children[i].classList.add('active');
            }
          });
          thumbsEl.appendChild(thumb);

          // Dots for mobile
          if (dotsEl) {
            var dot = document.createElement('span');
            dot.className = 'nitric-lp-fbt-popup-dot' + (i === 0 ? ' active' : '');
            dot.addEventListener('click', function() {
              imgEl.src = imgUrl;
              thumbsEl.querySelectorAll('.nitric-lp-fbt-popup-thumb').forEach(function(t) { t.classList.remove('active'); });
              thumbsEl.children[i].classList.add('active');
              dotsEl.querySelectorAll('.nitric-lp-fbt-popup-dot').forEach(function(d) { d.classList.remove('active'); });
              dot.classList.add('active');
            });
            dotsEl.appendChild(dot);
          }
        });
      }

      // Title
      titleEl.textContent = product.title;

      // Select first available variant
      var firstAvailable = product.variants.find(function(v) { return v.available; }) || product.variants[0];
      selectedVariantId = firstAvailable.id;
      updatePopupPrice(firstAvailable);

      // Description - show full product description with scroll
      if (product.description) {
        descEl.innerHTML = product.description;
      } else {
        descEl.innerHTML = '';
      }

      // Variants
      variantsEl.innerHTML = '';
      if (product.variants.length > 1) {
        product.variants.forEach(function(variant) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'nitric-lp-fbt-popup-variant-btn';
          if (variant.id === selectedVariantId) btn.classList.add('active');
          if (!variant.available) btn.disabled = true;
          btn.textContent = variant.title;
          btn.setAttribute('data-variant-id', variant.id);
          btn.addEventListener('click', function() {
            selectedVariantId = variant.id;
            updatePopupPrice(variant);
            variantsEl.querySelectorAll('.nitric-lp-fbt-popup-variant-btn').forEach(function(b) {
              b.classList.remove('active');
            });
            btn.classList.add('active');
          });
          variantsEl.appendChild(btn);
        });
      }

      // ATC button state
      atcBtn.disabled = !firstAvailable.available;
      atcBtn.textContent = firstAvailable.available ? 'Add to Cart' : 'Sold Out';

      loadingEl.classList.remove('active');
      contentEl.classList.add('active');
    }

    function updatePopupPrice(variant) {
      priceEl.textContent = formatMoney(variant.price);
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareEl.textContent = formatMoney(variant.compare_at_price);
        compareEl.style.display = 'inline';
      } else {
        compareEl.style.display = 'none';
      }
      atcBtn.disabled = !variant.available;
      atcBtn.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
    }

    function closePopup() {
      overlay.classList.remove('active');
      document.body.style.overflow = '';
    }

    // Close handlers
    closeBtn.addEventListener('click', closePopup);
    overlay.addEventListener('click', function(e) {
      if (e.target === overlay) closePopup();
    });
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && overlay.classList.contains('active')) closePopup();
    });

    // Add to cart
    atcBtn.addEventListener('click', function() {
      if (!selectedVariantId) return;

      atcBtn.classList.add('loading');
      atcBtn.disabled = true;

      var rootUrl = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
      fetch(rootUrl + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: selectedVariantId, quantity: 1 }] })
      })
      .then(function(res) { return res.json(); })
      .then(function() {
        atcBtn.classList.remove('loading');
        atcBtn.disabled = false;
        atcBtn.textContent = '✓ Added!';
        document.dispatchEvent(new CustomEvent('cart:updated'));
        setTimeout(function() {
          atcBtn.textContent = 'Add to Cart';
          closePopup();
        }, 1000);
      })
      .catch(function() {
        atcBtn.classList.remove('loading');
        atcBtn.disabled = false;
        atcBtn.textContent = 'Add to Cart';
        alert('Error adding to cart. Please try again.');
      });
    });
  }

  /**
   * IMAGE ZOOM LIGHTBOX
   * Fullscreen zoom with navigation: scroll/pinch to zoom, drag to pan, swipe to navigate.
   */
  function initImageZoom() {
    var overlay = document.getElementById('nitricZoomOverlay');
    var zoomImg = document.getElementById('nitricZoomImg');
    var zoomWrapper = document.getElementById('nitricZoomWrapper');
    var closeBtn = document.getElementById('nitricZoomClose');
    var prevBtn = document.getElementById('nitricZoomPrev');
    var nextBtn = document.getElementById('nitricZoomNext');
    var counterEl = document.getElementById('nitricZoomCounter');
    var mainImageEl = document.getElementById('nitricMainImage');

    if (!overlay || !zoomImg || !mainImageEl) return;

    var images = [];
    var currentZoomIndex = 0;
    var scale = 1;
    var translateX = 0;
    var translateY = 0;
    var isDragging = false;
    var startX = 0;
    var startY = 0;
    var lastTranslateX = 0;
    var lastTranslateY = 0;
    var initialPinchDistance = 0;
    var initialScale = 1;

    // Rebuilt each time zoom opens so it matches the gallery's current visible order.
    function collectImages() {
      images = [];
      var visibleDots = [];
      var dots = document.querySelectorAll('.nitric-lp-dot');
      dots.forEach(function(dot) {
        if (dot.classList.contains('nitric-lp-dot--hidden')) return;
        var url = dot.getAttribute('data-image');
        if (url) {
          images.push(url);
          visibleDots.push(dot);
        }
      });

      if (images.length === 0) {
        var currentImg = document.getElementById('nitricCurrentImage');
        if (currentImg && currentImg.src) images.push(currentImg.src);
      }
      return visibleDots;
    }

    // Open zoom on main image click/tap
    mainImageEl.addEventListener('click', function(e) {
      if (e.target.closest('.nitric-lp-carousel-dots')) return;
      var visibleDots = collectImages();
      var activeDot = document.querySelector('.nitric-lp-dot.active');
      var idx = activeDot ? visibleDots.indexOf(activeDot) : -1;
      currentZoomIndex = idx > -1 ? idx : 0;
      openZoom();
    });

    function openZoom() {
      if (images.length === 0) return;
      scale = 1;
      translateX = 0;
      translateY = 0;
      applyTransform();
      zoomImg.src = images[currentZoomIndex];
      updateCounter();
      overlay.classList.add('active');
      document.body.style.overflow = 'hidden';
    }

    function closeZoom() {
      overlay.classList.remove('active');
      document.body.style.overflow = '';
      scale = 1;
      translateX = 0;
      translateY = 0;
      applyTransform();
    }

    function showImage(index) {
      if (index < 0) index = images.length - 1;
      if (index >= images.length) index = 0;
      currentZoomIndex = index;
      scale = 1;
      translateX = 0;
      translateY = 0;
      applyTransform();
      zoomImg.src = images[currentZoomIndex];
      updateCounter();
    }

    function updateCounter() {
      if (counterEl && images.length > 1) {
        counterEl.textContent = (currentZoomIndex + 1) + ' / ' + images.length;
      }
    }

    function applyTransform() {
      zoomImg.style.transform = 'translate(' + translateX + 'px, ' + translateY + 'px) scale(' + scale + ')';
    }

    // Close button
    closeBtn.addEventListener('click', function(e) {
      e.stopPropagation();
      closeZoom();
    });

    // Click overlay background to close (only if not zoomed in)
    overlay.addEventListener('click', function(e) {
      if (e.target === overlay || e.target === zoomWrapper) {
        if (scale <= 1) closeZoom();
      }
    });

    // Navigation buttons
    prevBtn.addEventListener('click', function(e) {
      e.stopPropagation();
      showImage(currentZoomIndex - 1);
    });

    nextBtn.addEventListener('click', function(e) {
      e.stopPropagation();
      showImage(currentZoomIndex + 1);
    });

    // Keyboard navigation
    document.addEventListener('keydown', function(e) {
      if (!overlay.classList.contains('active')) return;
      if (e.key === 'Escape') closeZoom();
      if (e.key === 'ArrowLeft') showImage(currentZoomIndex - 1);
      if (e.key === 'ArrowRight') showImage(currentZoomIndex + 1);
    });

    // Desktop: scroll to zoom
    zoomWrapper.addEventListener('wheel', function(e) {
      e.preventDefault();
      var delta = e.deltaY > 0 ? -0.2 : 0.2;
      scale = Math.min(Math.max(scale + delta, 1), 4);
      if (scale <= 1) { translateX = 0; translateY = 0; }
      applyTransform();
    }, { passive: false });

    // Desktop: drag to pan when zoomed
    zoomWrapper.addEventListener('mousedown', function(e) {
      if (scale <= 1) return;
      e.preventDefault();
      isDragging = true;
      startX = e.clientX;
      startY = e.clientY;
      lastTranslateX = translateX;
      lastTranslateY = translateY;
      zoomWrapper.style.cursor = 'grabbing';
    });

    document.addEventListener('mousemove', function(e) {
      if (!isDragging) return;
      translateX = lastTranslateX + (e.clientX - startX);
      translateY = lastTranslateY + (e.clientY - startY);
      applyTransform();
    });

    document.addEventListener('mouseup', function() {
      if (isDragging) {
        isDragging = false;
        zoomWrapper.style.cursor = '';
      }
    });

    // Mobile: pinch to zoom and drag to pan
    var touches = {};

    zoomWrapper.addEventListener('touchstart', function(e) {
      if (e.touches.length === 2) {
        initialPinchDistance = getPinchDistance(e.touches);
        initialScale = scale;
      } else if (e.touches.length === 1) {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        lastTranslateX = translateX;
        lastTranslateY = translateY;
        touches.startTime = Date.now();
        touches.moved = false;
      }
    }, { passive: true });

    zoomWrapper.addEventListener('touchmove', function(e) {
      if (e.touches.length === 2) {
        e.preventDefault();
        var distance = getPinchDistance(e.touches);
        scale = Math.min(Math.max(initialScale * (distance / initialPinchDistance), 1), 4);
        if (scale <= 1) { translateX = 0; translateY = 0; }
        applyTransform();
      } else if (e.touches.length === 1 && scale > 1) {
        e.preventDefault();
        translateX = lastTranslateX + (e.touches[0].clientX - startX);
        translateY = lastTranslateY + (e.touches[0].clientY - startY);
        applyTransform();
        touches.moved = true;
      } else if (e.touches.length === 1 && scale <= 1) {
        touches.moved = true;
      }
    }, { passive: false });

    zoomWrapper.addEventListener('touchend', function(e) {
      if (e.touches.length === 0 && scale <= 1 && touches.moved) {
        var endX = e.changedTouches[0].clientX;
        var diff = startX - endX;
        if (Math.abs(diff) > 60) {
          if (diff > 0) showImage(currentZoomIndex + 1);
          else showImage(currentZoomIndex - 1);
        }
      }
      // Double-tap to zoom
      if (e.touches.length === 0 && !touches.moved) {
        var now = Date.now();
        if (touches.lastTap && now - touches.lastTap < 300) {
          if (scale > 1) {
            scale = 1; translateX = 0; translateY = 0;
          } else {
            scale = 2.5;
          }
          applyTransform();
          touches.lastTap = 0;
        } else {
          touches.lastTap = now;
        }
      }
    }, { passive: true });

    function getPinchDistance(touchList) {
      var dx = touchList[0].clientX - touchList[1].clientX;
      var dy = touchList[0].clientY - touchList[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }
  }

})();